import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTextDomainContext } from "@/app/providers";
import {
  useAddCompanyToTenant,
  useAddUserByAdmin,
  useAddUserToPartner,
  useAddUserToTenant,
  useCountries,
  usePartnersList,
  useRolesList,
  useUpdateUser,
} from "@/hooks";
import {
  Box,
  Button,
  Drawer,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { getRoleTypeNameFromKey, languageList } from "@/utils";
import {
  CountryDropdown,
  CustomerDropdown,
  LanguageDropdown,
  PartnerDropdown,
  PasswordInput,
  RolesDropdown,
  SelectDropdown,
  StatusDropdown,
  TenantDropdown,
} from "@/components";
import type { DropdownOption } from "@/types";
import { AddTenant } from "@/pages";

interface AddUserRow {
  id?: string;
  name?: string;
  email?: string;
  lang?: string;
  countryCode?: string;
  status?: string;
  isVerified?: boolean;
  roles?: string[];
  partner?: {
    key?: string;
  };
  [key: string]: any;
}

interface AddUserProps {
  open?: boolean;
  handleClose: () => void;
  row: AddUserRow;
}

interface AddUserInitialValue {
  timezonesList: string[];
  inputName: string;
  inputEmail: string;
  inputLang: any;
  inputCountry: any;
  inputStatus: {
    label: string;
    value: string;
  };
  inputRole: any;
  inputPartner: any;
  inputTenant: any;
  inputPassword: string;
  inputIsVerified: boolean;
}

export const AddUser = (props: AddUserProps): React.ReactElement => {
  //#region Data
  const { enqueueSnackbar } = useSnackbar();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const { countries, loading: countriesLoading } = useCountries();
  const { rolesList } = useRolesList();
  const { partnerList } = usePartnersList();
  const languages = languageList(gettext);
  const { addUserToPartner } = useAddUserToPartner();
  const { addUserToTenant } = useAddUserToTenant();
  const { addCompanyToTenant } = useAddCompanyToTenant();
  const { row } = props;
  // const {user} = useUser();

  const initialValue = useMemo<AddUserInitialValue>(() => {
    let initialRole: any = null;
    let initialLanguage: any;
    let initialCountry: any;
    let initialPartner: any;

    if (props?.row?.roles) {
      if (props?.row?.roles && props?.row?.roles.length) {
        // props?.row?.roles[0] is a role key string, find the full Role object
        initialRole = rolesList.find(
          (obj) => obj?.key === props?.row?.roles[0],
        );
      } else {
        initialRole = rolesList.find((obj) => obj?.key === "company-user");
      }
    }

    initialLanguage =
      languages.find((language) => language.lang === props?.row?.lang) ||
      languages[0] ||
      null;

    initialCountry =
      countries.find((country) => country.code === props?.row?.countryCode) ||
      countries[0] ||
      null;

    initialPartner =
      partnerList.find((partner) => partner.key === props?.row?.partner?.key) ||
      partnerList[0] ||
      null;

    return {
      // userId: props.row.id || "",
      timezonesList: [],
      inputName: props.row?.name || "",
      inputEmail: props.row?.email || "",
      inputLang: initialLanguage,
      inputCountry: initialCountry,
      inputStatus: {
        label: gettext("Active"),
        value: "Active",
      },
      inputRole: initialRole,
      inputPartner: initialPartner,
      inputTenant: null,
      inputPassword: "",
      inputIsVerified: Boolean(props.row?.isVerified),
    };
  }, [countries, gettext, languages, partnerList, props.row, rolesList]);
  //#endregion Data

  //#region useState
  // const [userId, setUserId] = useState(initialValue.userId);
  const [inputNameNew, setInputNameNew] = useState("");
  const [inputEmailNew, setInputEmailNew] = useState("");
  const [inputLangNew, setInputLangNew] = useState("");
  const [inputCountryNew, setInputCountryNew] = useState("");
  const [inputStatusNew, setInputStatusNew] = useState("");
  const [inputRole, setInputRole] = useState(initialValue.inputRole);
  const [inputPartner, setInputPartner] = useState(initialValue.inputPartner);
  const [inputTenant, setInputTenant] = useState(initialValue.inputTenant);
  const [inputCompany, setInputCompany] = useState(initialValue.inputTenant);
  const [inputPassword, setInputPassword] = useState(
    initialValue.inputPassword,
  );
  const [inputIsVerifiedNew, setInputIsVerifiedNew] = useState<boolean | null>(
    null,
  );
  // const [roleType, setRoleType] = useState(inputRole?.key);
  const [addTenantFormOpen, setAddTenantFormOpen] = useState(false);
  const [editTenantRow, setEditTenantRow] = useState<Record<string, unknown>>(
    {},
  );
  const [tenantRefetch, setTenantRefetch] = useState(false);
  const [showTenantDropdown, setShowTenantDropdown] = useState(true);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  //#endregion useState

  //#region useMemo

  const roleType = useMemo(() => {
    return getRoleTypeNameFromKey(inputRole?.key);
  }, [inputRole]);

  const userId = useMemo(() => {
    return row?.id || null;
  }, [row]);

  const inputName = useMemo(() => {
    return inputNameNew || row?.name || "";
  }, [inputNameNew, row]);

  const inputEmail = useMemo(() => {
    return inputEmailNew || row?.email || "";
  }, [inputEmailNew, row]);

  const inputLang = useMemo(() => {
    return inputLangNew || row?.lang || initialValue.inputLang || "";
  }, [inputLangNew, initialValue, row]);

  const inputCountry = useMemo(() => {
    return (
      inputCountryNew ||
      row?.countryCode ||
      initialValue?.inputCountry?.code ||
      ""
    );
  }, [inputCountryNew, initialValue, row]);

  const inputStatus = useMemo(() => {
    const status =
      inputStatusNew || row?.status || initialValue.inputStatus || "";
    if (typeof status === "object") {
      return status;
    }
    return { label: status, value: status };
  }, [inputStatusNew, initialValue, row]);

  const inputIsVerified = useMemo(() => {
    if (inputIsVerifiedNew !== null) {
      return inputIsVerifiedNew;
    }
    return Boolean(row?.isVerified ?? initialValue.inputIsVerified);
  }, [inputIsVerifiedNew, initialValue.inputIsVerified, row?.isVerified]);

  //#endregion useMemo

  //#region useEffect

  // useEffect(() => {
  //   if (!inputCountry?.code) {
  //     if (countries.length) {
  //       if (!isEqual(inputCountry, countries[0])) {
  //         setInputCountry(countries[0]);
  //       }
  //     }
  //   }
  // }, [countries, initialValue, inputCountry]);

  // useEffect(() => {
  //   if (!inputPartner?.id) {
  //     const activeOptions =
  //       partnerList?.filter((item) => item.status === "Active") || [];
  //     if (activeOptions.length) {
  //       setInputPartner(activeOptions[0]);
  //     }
  //   }
  // }, [partnerList, inputPartner]);

  // useEffect(() => {
  //   if (!inputRole?.key) {
  //     if (rolesList.length) {
  //       // setInputRole(rolesList[rolesList.length - 1]);
  //       setInputRole(rolesList.find((obj) => obj.key === "company-user"));
  //     }
  //   }
  // }, [rolesList, inputRole]);

  // useEffect(() => {
  //   if (props?.row?.id && props?.row?.id !== userId) {
  //     const countryRow = countries.find(
  //       (country) => country.code === props?.row?.countryCode,
  //     );
  //     const languageRow = languages.find(
  //       (language) => language.lang === props?.row?.lang,
  //     );
  //     const roleRow = rolesList.find(
  //       (role) => role.role === props?.row?.roles[0],
  //     );
  //     const partnerRow = partnerList.find(
  //       (partner) => partner.key === props?.row?.partner?.key,
  //     );
  //
  //     // setUserId(props.row.id);
  //     // setInputName(props.row.name);
  //     // setInputEmail(props.row.email);
  //     // setInputCountry(countryRow || null);
  //     // setInputTimezone(timeZoneRow || null);
  //     // setInputLang(languageRow || null);
  //     // setInputRole(roleRow || null);
  //     // setInputPartner(partnerRow || null);
  //   }
  // }, [
  //   countries,
  //   languages,
  //   partnerList,
  //   props.row,
  //   rolesList,
  //   userId,
  // ]);

  // useEffect(() => {
  //   setRoleType(getRoleTypeNameFromKey(inputRole?.key));
  // }, [inputRole]);

  // useEffect(() => {
  //   if (addTenantFormOpen) {
  //     setShowTenantDropdown(false);
  //   } else {
  //     setTimeout(() => {
  //       setShowTenantDropdown(true);
  //     }, 2000);
  //   }
  // }, [addTenantFormOpen]);

  // useEffect(() => {
  //   if (inputTenant?.companies && !inputTenant?.companies?.length) {
  //     setShowCompanyDropdown(true);
  //   } else {
  //     setShowCompanyDropdown(false);
  //   }
  // }, [inputTenant]);
  //#endregion useEffect

  //#region Handlers
  const handleClose = () => {
    resetForm();
    props.handleClose();
  };

  const resetForm = useCallback(() => {
    setInputNameNew(initialValue.inputName);
    setInputEmailNew(initialValue.inputEmail);
    setInputPassword(initialValue.inputPassword);
    setInputIsVerifiedNew(null);
  }, [initialValue]);

  const [emailError, setEmailError] = useState(false);

  const { addUserByAdmin, loading: adding } = useAddUserByAdmin(
    () => {
      resetForm();
      handleClose();
      enqueueSnackbar(gettext("The new user has been added."), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const { updateUser, loading: updating } = useUpdateUser(
    () => {
      resetForm();
      handleClose();
      enqueueSnackbar(gettext("The user has been updated"), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(`Backend Error: ${error}`, {
          variant: "error",
        });
      }
    },
  );

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const roleType = getRoleTypeNameFromKey(inputRole?.key);
    const partnerRow = partnerList.find(
      (row) => row.id === inputTenant?.partner,
    );
    const partnerKey = partnerRow?.key;

    const variables: Record<string, any> = {
      name: inputName,
      email: inputEmail,
      countryCode: inputCountry?.code || inputCountry,
      lang: inputLang?.lang,
      status: (inputStatus as any)?.value || inputStatus,
      isVerified: inputIsVerified,
    };

    const variablesPartner = {
      email: inputEmail,
      languageCode: inputLang?.lang,
      partnerId: inputPartner?.id,
      roleKey: inputRole?.key,
      notifyUser: false,
    };

    const variablesCompany = {
      email: inputEmail,
      languageCode: inputLang?.lang,
      partnerKey: partnerKey,
      tenantId: inputTenant?.id,
      roleKey: inputRole?.key,
      notifyUser: false,
    };

    const variablesTenantCompany = {
      tenantId: inputTenant?.id,
      companyId: inputCompany?.value,
    };

    if (props.row.id) {
      variables.userId = props.row.id;
      updateUser({
        variables: variables,
        refetchQueries: [
          "AllUsers",
          "SystemUsers",
          "PartnerUsers",
          "CompanyUsers",
        ],
      }).then();
    } else {
      variables.password = inputPassword;
      variables.currentRoleKey = inputRole?.key;
      switch (roleType) {
        case "system":
          variables.roles = [inputRole?.key];
          break;
        case "partner":
          variables.currentPartnerId = inputPartner?.id;
          break;
        case "company":
          if (inputTenant?.companies?.length) {
            // inputTenant?.companies[0] is a company ID, but I do not have company name
          } else {
            variables.currentTeamId = inputCompany?.id;
            variables.currentTeam = inputCompany?.name;
          }
          variables.currentTenantId = inputTenant?.id;
          break;
        default:
          break;
      }

      addUserByAdmin({
        variables: variables,
        refetchQueries: [
          "AllUsers",
          "SystemUsers",
          "PartnerUsers",
          "CompanyUsers",
        ],
      }).then(() => {
        switch (roleType) {
          case "partner":
            addUserToPartner({
              variables: variablesPartner,
            }).then();
            break;
          case "company":
            addUserToTenant({
              variables: variablesCompany,
            }).then();

            if (showCompanyDropdown) {
              addCompanyToTenant({
                variables: variablesTenantCompany,
              }).then();
            }
            break;
          default:
            break;
        }
      });
    }
  };

  const handleNameChange = (value: string) => {
    setInputNameNew(value);
  };

  const handlePartnerChange = (event: any) => {
    setInputPartner(event);
  };

  const handleRoleChange = (event: any) => {
    setInputRole(event);
  };

  const handleTenantChange = (event: any) => {
    if (event?.companies?.length) {
      setShowCompanyDropdown(true);
    } else {
      setShowCompanyDropdown(false);
    }
    setInputTenant(event);
  };

  const handleCompanyChange = (event: any) => {
    setInputCompany(event);
  };

  const handleLanguageChange = (event: any) => {
    setInputLangNew(event);
  };

  const handleStatusChange = (event: any) => {
    setInputStatusNew(event);
  };

  const emailVerificationOptions = useMemo<DropdownOption[]>(
    () => [
      {
        value: "verified",
        label: gettext("Verified"),
        color: "success",
      },
      {
        value: "unverified",
        label: gettext("Unverified"),
        color: "error",
      },
    ],
    [gettext],
  );

  const handleEmailVerificationChange = (event: any) => {
    setInputIsVerifiedNew(event?.value === "verified");
  };

  const handleCloseAddTenantForm = (event: boolean) => {
    if (event) {
      setShowTenantDropdown(false);
    } else {
      setTimeout(() => {
        setShowTenantDropdown(true);
      }, 2000);
    }

    setAddTenantFormOpen(event);
    setEditTenantRow({});
    setTenantRefetch(true);
  };

  const tenantRefetched = () => {
    setTenantRefetch(false);
  };

  //#endregion Handlers

  return (
    <>
      <Drawer
        anchor={"right"}
        open={props.open}
        onClose={handleClose}
        sx={{
          zIndex: (theme) => theme.zIndex.appBar + 1,
        }}
        slotProps={{
          paper: {
            sx: {
              padding: 4,
              maxWidth: 720,
            },
          },
        }}
      >
        <Box
          sx={{
            width: "auto",
          }}
        >
          <form onSubmit={submitHandler}>
            <Stack
              spacing={4}
              sx={{
                height: "100%",
              }}
            >
              <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
                {!userId && gettext("Add New User")}
                {userId && gettext("Edit User")}
              </Typography>

              <Box
                sx={{
                  height: "100%",
                }}
              >
                <Grid
                  container
                  spacing={4}
                  sx={{
                    width: "100%",
                  }}
                >
                  {/*<Grid size={{xs: 12}}>*/}
                  {/*  <Divider>*/}
                  {/*    <Chip label={gettext("Basic Information")} size="small"/>*/}
                  {/*  </Divider>*/}
                  {/*</Grid>*/}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      type="text"
                      label={gettext("Name")}
                      placeholder={gettext("Enter name")}
                      size="small"
                      required
                      fullWidth
                      value={inputName}
                      onChange={(e) => handleNameChange(e.target.value)}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      type="email"
                      label={gettext("Email")}
                      placeholder={gettext("Enter email")}
                      size="small"
                      required
                      fullWidth
                      autoComplete="off"
                      value={inputEmail}
                      onChange={(e) => {
                        setInputEmailNew(e.target.value);
                        setEmailError(false);
                      }}
                      error={emailError}
                      helperText={
                        emailError &&
                        gettext("The mail already exists in the system!")
                      }
                    />
                  </Grid>

                  {!userId && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <PasswordInput
                        label={gettext("Password")}
                        placeholder={gettext("Enter password")}
                        size="small"
                        required
                        fullWidth
                        autoComplete="new-password"
                        value={inputPassword}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setInputPassword(e.target.value)
                        }
                      />

                      {/*<TextField*/}
                      {/*  type={passwordShown ? "text" : "password"}*/}
                      {/*  label={gettext("Password")}*/}
                      {/*  placeholder={gettext("Enter password")}*/}
                      {/*  size="small"*/}
                      {/*  required*/}
                      {/*  fullWidth*/}
                      {/*  autoComplete="new-password"*/}
                      {/*  value={inputPassword}*/}
                      {/*  onChange={(e) => setInputPassword(e.target.value)}*/}
                      {/*  slotProps={{*/}
                      {/*    input: {*/}
                      {/*      endAdornment: (*/}
                      {/*        <InputAdornment position="end">*/}
                      {/*          <IconButton*/}
                      {/*            aria-label="toggle password visibility"*/}
                      {/*            onClick={togglePassword}*/}
                      {/*            edge="end"*/}
                      {/*          >*/}
                      {/*            {passwordShown ? <VisibilityOff/> : <Visibility/>}*/}
                      {/*          </IconButton>*/}
                      {/*        </InputAdornment>*/}
                      {/*      ),*/}
                      {/*    },*/}
                      {/*  }}*/}
                      {/*/>*/}
                    </Grid>
                  )}

                  <Grid size={{ xs: 12, md: 6 }}>
                    <LanguageDropdown
                      defaultValue={inputLang?.value}
                      componentLabel={gettext("Languages")}
                      onChange={handleLanguageChange}
                      required
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <CountryDropdown
                      defaultValue={inputCountry}
                      onChange={(_e: unknown, newValue: string) => {
                        setInputCountryNew(newValue);
                      }}
                    />
                    {/*<Autocomplete*/}
                    {/*  disablePortal*/}
                    {/*  options={countries}*/}
                    {/*  getOptionLabel={(option) => option.name || ""}*/}
                    {/*  getOptionKey={(option) => option.code || ""}*/}
                    {/*  value={inputCountry}*/}
                    {/*  onChange={(e, newValue) => {*/}
                    {/*    setInputCountryNew(newValue);*/}
                    {/*    // setInputTimezone(initialValue.inputTimezone);*/}
                    {/*  }}*/}
                    {/*  size="small"*/}
                    {/*  fullWidth*/}
                    {/*  isOptionEqualToValue={(option, value) =>*/}
                    {/*    option?.value?.toLowerCase() ===*/}
                    {/*    value?.value?.toLowerCase()*/}
                    {/*  }*/}
                    {/*  renderInput={(params) => (*/}
                    {/*    <TextField*/}
                    {/*      {...params}*/}
                    {/*      required*/}
                    {/*      label={gettext("Country")}*/}
                    {/*    />*/}
                    {/*  )}*/}
                    {/*/>*/}

                    {/*<Autocomplete*/}
                    {/*  disablePortal*/}
                    {/*  options={languages}*/}
                    {/*  getOptionLabel={(option) => option.name || ""}*/}
                    {/*  getOptionKey={(option) => option.lang || ""}*/}
                    {/*  // options={[*/}
                    {/*  //   {*/}
                    {/*  //     label: gettext("English"),*/}
                    {/*  //     value: "en"*/}
                    {/*  //   },*/}
                    {/*  //   {*/}
                    {/*  //     label: gettext("Danish"),*/}
                    {/*  //     value: "da"*/}
                    {/*  //   },*/}
                    {/*  //   {*/}
                    {/*  //     label: gettext("German"),*/}
                    {/*  //     value: "de"*/}
                    {/*  //   }*/}
                    {/*  // ]}*/}
                    {/*  value={inputLang}*/}
                    {/*  onChange={(e, newValue) => setInputLang(newValue)}*/}
                    {/*  size="small"*/}
                    {/*  fullWidth*/}
                    {/*  isOptionEqualToValue={(option, value) =>*/}
                    {/*    option?.value?.toLowerCase() ===*/}
                    {/*    value?.value?.toLowerCase()*/}
                    {/*  }*/}
                    {/*  renderInput={(params) => (*/}
                    {/*    <TextField*/}
                    {/*      {...params}*/}
                    {/*      required*/}
                    {/*      label={gettext("Language")}*/}
                    {/*    />*/}
                    {/*  )}*/}
                    {/*/>*/}
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <StatusDropdown
                      visualStyle="autoComplete"
                      defaultValue={
                        typeof inputStatus === "string"
                          ? inputStatus
                          : inputStatus?.value
                      }
                      onChange={handleStatusChange}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <SelectDropdown
                      visualStyle="autoComplete"
                      componentLabel={gettext("Email Verification")}
                      defaultValue={inputIsVerified ? "verified" : "unverified"}
                      options={emailVerificationOptions}
                      onChange={handleEmailVerificationChange}
                    />
                  </Grid>

                  {!userId && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <RolesDropdown
                        defaultValue={inputRole?.value}
                        componentLabel={gettext("Roles")}
                        type="super"
                        onChange={handleRoleChange}
                        required
                      />

                      {/*<Autocomplete*/}
                      {/*  disablePortal*/}
                      {/*  options={rolesList}*/}
                      {/*  getOptionLabel={(option) => option.role || ""}*/}
                      {/*  getOptionKey={(option) => option.key || ""}*/}
                      {/*  value={inputRole}*/}
                      {/*  onChange={(e, newValue) => setInputRole(newValue)}*/}
                      {/*  size="small"*/}
                      {/*  fullWidth*/}
                      {/*  isOptionEqualToValue={(option, value) =>*/}
                      {/*    option?.value?.toLowerCase() ===*/}
                      {/*    value?.value?.toLowerCase()*/}
                      {/*  }*/}
                      {/*  renderInput={(params) => (*/}
                      {/*    <TextField {...params} required label={gettext("Role")} />*/}
                      {/*  )}*/}
                      {/*/>*/}
                    </Grid>
                  )}

                  {roleType === "partner" && !userId && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <PartnerDropdown
                        defaultValue={inputPartner?.value}
                        componentLabel={gettext("Partners")}
                        onChange={handlePartnerChange}
                      />
                    </Grid>
                  )}

                  {roleType === "company" && !userId && (
                    <>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Box
                            sx={{ maxWidth: "calc(100% - 70px)", flexGrow: 1 }}
                          >
                            {showTenantDropdown && (
                              <TenantDropdown
                                defaultValue={inputTenant?.value}
                                onChange={handleTenantChange}
                                refetch={tenantRefetch}
                                refetched={tenantRefetched}
                                required
                                sx={{
                                  maxWidth: "auto",
                                }}
                              />
                            )}
                          </Box>

                          <Button
                            // variant="contained"
                            variant="outlined"
                            // color="secondary"
                            onClick={() => {
                              setAddTenantFormOpen(true);
                            }}
                          >
                            {gettext("New")}
                          </Button>
                        </Box>
                      </Grid>
                    </>
                  )}

                  {roleType === "company" && !userId && (
                    <>
                      {showCompanyDropdown && (
                        <Grid size={{ xs: 12, md: 6 }}>
                          <CustomerDropdown
                            defaultValue={inputCompany?.value}
                            onChange={handleCompanyChange}
                            componentLabel={gettext("Company")}
                            customerType="parent"
                            fullWidth
                            required
                            currentTeamId={inputCompany?.id}
                            currentTenantId={inputTenant?.id}
                          />
                        </Grid>
                      )}
                    </>
                  )}

                  {/*<Grid size={{xs: 12}}>*/}
                  {/*  <Divider>*/}
                  {/*    <Chip label={gettext("Role Specific Information")} size="small"/>*/}
                  {/*  </Divider>*/}
                  {/*</Grid>*/}

                  {/*<Grid size={{xs: 12, md: 6}}>*/}
                  {/*  <Autocomplete*/}
                  {/*    disablePortal*/}
                  {/*    options={timezonesList}*/}
                  {/*    getOptionLabel={(option) => option.zoneName || ""}*/}
                  {/*    getOptionKey={(option) => option.zoneName || ""}*/}
                  {/*    value={inputTimezone}*/}
                  {/*    onChange={(e, newValue) => setInputTimezone(newValue)}*/}
                  {/*    size="small"*/}
                  {/*    fullWidth*/}
                  {/*    isOptionEqualToValue={(option, value) =>*/}
                  {/*      option?.value?.toLowerCase() ===*/}
                  {/*      value?.value?.toLowerCase()*/}
                  {/*    }*/}
                  {/*    renderInput={(params) => (*/}
                  {/*      <TextField*/}
                  {/*        {...params}*/}
                  {/*        required*/}
                  {/*        label={gettext("Timezone")}*/}
                  {/*      />*/}
                  {/*    )}*/}
                  {/*  />*/}
                  {/*</Grid>*/}
                </Grid>
              </Box>

              <Paper elevation={0}>
                <Grid
                  container
                  spacing={4}
                  sx={{
                    width: "100%",
                  }}
                >
                  <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => handleClose()}
                    >
                      {gettext("Cancel")}
                    </Button>
                  </Grid>

                  <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                    <Button
                      variant="contained"
                      loading={adding || updating || countriesLoading}
                      fullWidth
                      type="submit"
                    >
                      {gettext("Save")}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            </Stack>
          </form>
        </Box>
      </Drawer>
      <AddTenant
        open={addTenantFormOpen}
        handleClose={handleCloseAddTenantForm}
        row={editTenantRow}
      />
    </>
  );
};
